import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Phone, MapPin, MessageCircle, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t('contact.success'));
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar mensaje');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        titleES="Contacto — Colegio ARCE | Escuela de Español Online"
        titleEN="Contact — Colegio ARCE | Online Spanish School"
        descES="Contacta a Colegio ARCE: laura@colegioarce.com. Sede en Medford, MA 02155. Agenda una cita gratuita o escríbenos por WhatsApp."
        descEN="Contact Colegio ARCE: laura@colegioarce.com. Based in Medford, MA 02155. Schedule a free consultation or reach us via WhatsApp."
        path="/contact"
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact Colegio ARCE",
          "url": "https://www.colegioarce.com/contact",
          "mainEntity": {
            "@type": "EducationalOrganization",
            "name": "Colegio ARCE",
            "email": "laura@colegioarce.com",
            "address": { "@type": "PostalAddress", "addressLocality": "Medford", "addressRegion": "MA", "postalCode": "02155", "addressCountry": "US" }
          }
        }}
        breadcrumbs={[
          { nameES: 'Inicio', nameEN: 'Home', path: '/' },
          { nameES: 'Contacto', nameEN: 'Contact', path: '/contact' }
        ]}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-wide uppercase text-[#E63946] font-semibold mb-4">
            {t('contact.title')}
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1D1D1F] mb-6" data-testid="contact-title">
            {t('contact.subtitle')}
          </h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-8">
                {language === 'es' ? 'Envíanos un mensaje' : 'Send us a message'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('contact.name')}</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="py-6 rounded-xl"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('contact.email')}</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="py-6 rounded-xl"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('contact.phone')}</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="py-6 rounded-xl"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">{t('contact.subject')}</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="py-6 rounded-xl"
                      required
                      data-testid="contact-subject-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{t('contact.message')}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="min-h-[150px] rounded-xl"
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 rounded-xl bg-[#E63946] hover:bg-[#D62828]"
                  data-testid="contact-submit-btn"
                >
                  {loading ? 'Enviando...' : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      {t('contact.send')}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div>
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-8">
                {language === 'es' ? 'Información de contacto' : 'Contact Information'}
              </h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-[#E63946]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1D1D1F]">{t('contact.location')}</h3>
                    <p className="text-[#86868B]">Medford, MA 02155<br />United States</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-[#E63946]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1D1D1F]">Email</h3>
                    <a href="mailto:laura@colegioarce.com" className="text-[#86868B] hover:text-[#E63946] transition-colors">
                      laura@colegioarce.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="h-6 w-6 text-[#25D366]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1D1D1F]">WhatsApp</h3>
                    <a 
                      href="https://wa.me/13392277556" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#86868B] hover:text-[#25D366] transition-colors"
                      data-testid="contact-whatsapp"
                    >
                      {language === 'es' ? 'Enviar mensaje' : 'Send message'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="rounded-2xl overflow-hidden h-[300px] bg-[#F5F5F7]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47180.96754393666!2d-71.13766705!3d42.4184296!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e370bf60c80b21%3A0x5fa6e8a8ad1e2c8c!2sMedford%2C%20MA!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Colegio ARCE Location"
                  data-testid="contact-map"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
